import { type ChangeEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { Button, IconButton } from './primitives';

/**
 * FileInput (owner, 2026-10-13: "the photo file button looks like an old Windows XP button"): the one file picker of every
 * Vello web app. The browser's own `<input type="file">` is never drawn - it stays in the DOM, hidden, and the design's
 * 48px `Button` opens it. Beside the button it shows the chosen file name (an image also gets a thumbnail), and a clear
 * action puts the field back to empty.
 *
 * Use it inside a `Field`: `id` is the control's id, so the field's label points at the button (a click on the label opens the
 * picker) and the field's `aria-describedby` / `aria-invalid` / `aria-required` land on it. All visible text arrives as props
 * (this package holds no language): `chooseLabel` ("Choose file"), `replaceLabel` ("Replace file", shown once a file is
 * chosen), `emptyLabel` ("No file chosen"), `clearLabel` ("Remove file").
 *
 * Controlled with `value` (an array of files - pass `[]` to clear it from outside) or uncontrolled. `accept` and `multiple`
 * are the native ones. Enter and Space on the button open the picker; the file list is a live region, so a screen reader
 * hears the chosen name.
 */
export interface FileInputProps {
  /** The id of the button (the `Field`'s `htmlFor`). */
  readonly id: string;
  readonly name?: string;
  /** The native `accept` filter (`image/png,image/jpeg`, `.pdf`). */
  readonly accept?: string;
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly invalid?: boolean;
  /** The chosen files; omit for an uncontrolled field. */
  readonly value?: readonly File[];
  readonly onChange?: (files: readonly File[]) => void;
  /** The button before anything is chosen. */
  readonly chooseLabel: string;
  /** The button once a file is chosen (default: `chooseLabel`). */
  readonly replaceLabel?: string;
  /** Shown beside the button while nothing is chosen. */
  readonly emptyLabel: string;
  /** The accessible name of the clear action. */
  readonly clearLabel: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean | 'true' | 'false';
  readonly 'aria-required'?: boolean | 'true' | 'false';
  readonly className?: string;
}

/** The same array while its files are the same files: a parent that rebuilds `[file]` every render must not restart the thumbnails. */
function useStableFiles(files: readonly File[]): readonly File[] {
  const kept = useRef(files);
  if (kept.current.length !== files.length || kept.current.some((f, i) => f !== files[i])) kept.current = files;
  return kept.current;
}

const isImage = (file: File): boolean => file.type.startsWith('image/');

/** Object URLs for the image files of a selection, revoked when the selection changes or the field unmounts. */
function useThumbnails(files: readonly File[]): ReadonlyMap<File, string> {
  const [urls, setUrls] = useState<ReadonlyMap<File, string>>(new Map());
  useEffect(() => {
    const create = typeof URL !== 'undefined' ? (URL as { createObjectURL?: (b: Blob) => string }).createObjectURL : undefined;
    if (create === undefined) {
      setUrls(new Map());
      return;
    }
    const made = new Map<File, string>();
    for (const f of files) if (isImage(f)) made.set(f, create.call(URL, f));
    setUrls(made);
    return () => {
      for (const u of made.values()) URL.revokeObjectURL?.(u);
    };
  }, [files]);
  return urls;
}

export function FileInput({
  id,
  name,
  accept,
  multiple = false,
  disabled = false,
  required = false,
  invalid = false,
  value,
  onChange,
  chooseLabel,
  replaceLabel,
  emptyLabel,
  clearLabel,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  className,
}: FileInputProps): ReactElement {
  const native = useRef<HTMLInputElement>(null);
  const [own, setOwn] = useState<readonly File[]>([]);
  const files = useStableFiles(value ?? own);
  const thumbs = useThumbnails(files);

  // Keep the hidden native input in step when the owner clears the selection from outside.
  useEffect(() => {
    if (files.length === 0 && native.current !== null && native.current.value !== '') native.current.value = '';
  }, [files]);

  const commit = (next: readonly File[]): void => {
    if (value === undefined) setOwn(next);
    onChange?.(next);
  };
  const picked = (e: ChangeEvent<HTMLInputElement>): void => {
    const list = e.target.files === null ? [] : Array.from(e.target.files);
    // Cancelling the picker on a browser that empties the list must not wipe a choice already made.
    if (list.length === 0) return;
    commit(multiple ? list : list.slice(0, 1));
  };
  const clear = (): void => {
    if (native.current !== null) native.current.value = '';
    commit([]);
    native.current?.parentElement?.querySelector<HTMLButtonElement>('button.vello-fileinput__button')?.focus();
  };

  return (
    <div className={`vello-fileinput${className !== undefined ? ` ${className}` : ''}`} data-invalid={invalid ? 'true' : undefined}>
      <input ref={native} type="file" name={name} accept={accept} multiple={multiple} disabled={disabled} hidden tabIndex={-1} aria-hidden="true" onChange={picked} data-fileinput-native="true" data-target-exempt="hidden native input, never shown or reached: the 48px Button opens it" />
      <Button
        id={id}
        type="button"
        variant="secondary"
        className="vello-fileinput__button"
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
        aria-required={ariaRequired ?? (required ? true : undefined)}
        onClick={() => native.current?.click()}
      >
        {files.length > 0 ? (replaceLabel ?? chooseLabel) : chooseLabel}
      </Button>
      <div className="vello-fileinput__files" aria-live="polite">
        {files.length === 0 ? (
          <span className="vello-fileinput__empty">{emptyLabel}</span>
        ) : (
          <ul className="vello-fileinput__list">
            {files.map((f, i) => {
              const thumb = thumbs.get(f);
              return (
                <li key={`${f.name}-${i}`} className="vello-fileinput__file">
                  {thumb !== undefined && <img className="vello-fileinput__thumb" src={thumb} alt="" />}
                  <span className="vello-fileinput__name" title={f.name}>
                    {f.name}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {files.length > 0 && !disabled && <IconButton icon="x" label={clearLabel} className="vello-fileinput__clear" onClick={clear} />}
    </div>
  );
}
